import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { TextArea } from '../../../components/TextArea';

import { Trash2, Edit3, Plus, X } from 'lucide-react';
import { api } from '../../../services/api';
import { enqueueSnackbar } from 'notistack';
import { CampoTipo, CreateCampoTipoDTO, UpdateCampoTipoDTO, TipoCampo, TipoValidacao } from '../../../types/grupos';

interface CamposTiposModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
  camposTipos: CampoTipo[];
}

interface FormData {
  nome: string;
  descricao: string;
  tipo_campo: TipoCampo;
  tipo_validacao: TipoValidacao;
  decimal: boolean;
  opcoes: string[];
  valor_padrao: string;
  placeholder: string;
  ajuda: string;
  ativo: boolean;
}

const initialFormData: FormData = {
  nome: '',
  descricao: '',
  tipo_campo: 'text',
  tipo_validacao: 'none',
  decimal: false,
  opcoes: [],
  valor_padrao: '',
  placeholder: '',
  ajuda: '',
  ativo: true
};

const CamposTiposModal: React.FC<CamposTiposModalProps> = ({
  isOpen,
  onClose,
  onUpdate,
  camposTipos
}) => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [novaOpcao, setNovaOpcao] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setFormData(initialFormData);
      setEditingId(null);
      setShowForm(false);
      setNovaOpcao('');
    }
  }, [isOpen]);

  const handleEdit = (campo: CampoTipo) => {
    // `tipo_validacao` não existe no response da API — derivamos de `validacao.required`
    const tipoValidacaoDerivado: TipoValidacao =
      campo.validacao?.required ? 'required' : 'none';

    setFormData({
      nome: campo.nome,
      descricao: campo.descricao || '',
      tipo_campo: (campo.tipo_input as TipoCampo) || 'text',
      tipo_validacao: tipoValidacaoDerivado,
      decimal: !!(campo.validacao?.step && campo.validacao.step < 1),
      opcoes: campo.opcoes ? campo.opcoes.map(o => o.label) : [],
      valor_padrao: campo.valor_padrao || '',
      placeholder: campo.placeholder || '',
      ajuda: campo.ajuda || '',
      ativo: campo.ativo
    });
    setEditingId(campo.id);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja deletar este tipo de campo?')) return;

    try {
      await api.delete(`/campos-tipos/${id}`);
      enqueueSnackbar('Tipo de campo deletado com sucesso!', { variant: 'success' });
      onUpdate();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Erro ao deletar tipo de campo', { variant: 'error' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      if (editingId) {
        const updateData: UpdateCampoTipoDTO = {
          ...formData
        };
        await api.put(`/campos-tipos/${editingId}`, updateData);
        enqueueSnackbar('Tipo de campo atualizado com sucesso!', { variant: 'success' });
      } else {
        const createData: CreateCampoTipoDTO = {
          ...formData
        };
        await api.post('/campos-tipos', createData);
        enqueueSnackbar('Tipo de campo criado com sucesso!', { variant: 'success' });
      }

      setFormData(initialFormData);
      setEditingId(null);
      setShowForm(false);
      setNovaOpcao('');
      onUpdate();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Erro ao salvar tipo de campo', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const adicionarOpcao = () => {
    if (novaOpcao.trim()) {
      setFormData(prev => ({
        ...prev,
        opcoes: [...prev.opcoes, novaOpcao.trim()]
      }));
      setNovaOpcao('');
    }
  };

  const removerOpcao = (index: number) => {
    setFormData(prev => ({
      ...prev,
      opcoes: prev.opcoes.filter((_, i) => i !== index)
    }));
  };

  const tiposCampo = [
    { value: 'text', label: 'Texto' },
    { value: 'number', label: 'Número' },
    { value: 'email', label: 'E-mail' },
    { value: 'tel', label: 'Telefone' },
    { value: 'url', label: 'URL' },
    { value: 'date', label: 'Data' },
    { value: 'datetime', label: 'Data e Hora' },
    { value: 'textarea', label: 'Texto Longo' },
    { value: 'select', label: 'Seleção Simples' },
    { value: 'multiselect', label: 'Seleção Múltipla' },
    { value: 'radio', label: 'Opção Única' },
    { value: 'checkbox', label: 'Múltipla Escolha' },
    { value: 'file', label: 'Arquivo' }
  ];

  const tiposValidacao = [
    { value: 'none', label: 'Nenhuma' },
    { value: 'required', label: 'Obrigatório' },
    { value: 'email', label: 'E-mail válido' },
    { value: 'url', label: 'URL válida' },
    { value: 'min_length', label: 'Tamanho mínimo' },
    { value: 'max_length', label: 'Tamanho máximo' },
    { value: 'pattern', label: 'Padrão regex' },
    { value: 'numeric', label: 'Apenas números' },
    { value: 'alphanumeric', label: 'Alfanumérico' }
  ];

  const requerOpcoes = ['select', 'multiselect', 'radio', 'checkbox'].includes(formData.tipo_campo);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Gerenciar Tipos de Campos</DialogTitle>
          <DialogDescription>
            Configure os tipos de campos disponíveis para os grupos e suas validações.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Botão para adicionar novo campo */}
          {!showForm && (
            <Button
              onClick={() => setShowForm(true)}
              className="w-full"
              variant="outline"
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Novo Tipo de Campo
            </Button>
          )}

          {/* Formulário */}
          {showForm && (
            <div className="border rounded-lg p-4 bg-muted/40">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium">
                  {editingId ? 'Editar Tipo de Campo' : 'Novo Tipo de Campo'}
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowForm(false);
                    setFormData(initialFormData);
                    setEditingId(null);
                    setNovaOpcao('');
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="nome">Nome do Campo *</Label>
                    <Input
                      id="nome"
                      value={formData.nome}
                      onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                      placeholder="Ex: Telefone, Data de Fundação..."
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tipo_campo">Tipo de Campo *</Label>
                    <select
                      id="tipo_campo"
                      value={formData.tipo_campo}
                      onChange={(e) => setFormData(prev => ({ ...prev, tipo_campo: e.target.value as TipoCampo }))}
                      className="flex h-10 w-full appearance-none rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      required
                    >
                      <option value="">Selecione o tipo</option>
                      {tiposCampo.map(tipo => (
                        <option key={tipo.value} value={tipo.value}>
                          {tipo.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Configuração de opções — exibida LOGO após o tipo para máxima visibilidade */}
                {requerOpcoes && (
                  <div className="rounded-lg border border-dashed border-primary/50 bg-primary/5 p-4 space-y-3">
                    <Label className="text-sm font-semibold text-primary">
                      Opções do Campo *
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Adicione cada opção que estará disponível para seleção neste campo.
                    </p>
                    <div className="flex gap-2">
                      <Input
                        value={novaOpcao}
                        onChange={(e) => setNovaOpcao(e.target.value)}
                        placeholder="Digite uma opção e pressione Enter ou +"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            adicionarOpcao();
                          }
                        }}
                      />
                      <Button type="button" onClick={adicionarOpcao} size="sm">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>

                    {formData.opcoes.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {formData.opcoes.map((opcao, index) => (
                          <Badge key={index} variant="secondary" className="flex items-center gap-1 pr-1">
                            <span>{opcao}</span>
                            <button
                              type="button"
                              onClick={() => removerOpcao(index)}
                              className="ml-1 rounded hover:bg-destructive/20 text-destructive"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground italic">
                        Nenhuma opção adicionada ainda.
                      </p>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <TextArea
                    name="descricao"
                    title="Descrição"
                    value={formData.descricao}
                    onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                    placeholder="Descreva o propósito deste campo..."
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="tipo_validacao">Validação</Label>
                    <select
                      id="tipo_validacao"
                      value={formData.tipo_validacao}
                      onChange={(e) => setFormData(prev => ({ ...prev, tipo_validacao: e.target.value as TipoValidacao }))}
                      className="flex h-10 w-full appearance-none rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">Selecione a validação</option>
                      {tiposValidacao.map(tipo => (
                        <option key={tipo.value} value={tipo.value}>
                          {tipo.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {formData.tipo_campo === 'number' ? (
                    <div className="flex items-center space-x-2 pt-6">
                      <Switch
                        id="decimal"
                        checked={formData.decimal}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, decimal: checked }))}
                      />
                      <Label htmlFor="decimal">Permitir Decimais</Label>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label htmlFor="valor_padrao">Valor Padrão</Label>
                      <Input
                        id="valor_padrao"
                        value={formData.valor_padrao}
                        onChange={(e) => setFormData(prev => ({ ...prev, valor_padrao: e.target.value }))}
                        placeholder="Valor inicial do campo"
                      />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="placeholder">Placeholder</Label>
                    <Input
                      id="placeholder"
                      value={formData.placeholder}
                      onChange={(e) => setFormData(prev => ({ ...prev, placeholder: e.target.value }))}
                      placeholder="Texto de exemplo no campo"
                    />
                  </div>

                  <div className="flex items-center space-x-2 pt-6">
                    <Switch
                      id="ativo"
                      checked={formData.ativo}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, ativo: checked }))}
                    />
                    <Label htmlFor="ativo">Campo Ativo</Label>
                  </div>
                </div>

                <div className="space-y-2">
                  <TextArea
                    name="ajuda"
                    title="Texto de Ajuda"
                    value={formData.ajuda}
                    onChange={(e) => setFormData(prev => ({ ...prev, ajuda: e.target.value }))}
                    placeholder="Instruções para preenchimento do campo..."
                    rows={2}
                  />
                </div>

                <div className="flex justify-end space-x-2 pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowForm(false);
                      setFormData(initialFormData);
                      setEditingId(null);
                      setNovaOpcao('');
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting || (requerOpcoes && formData.opcoes.length === 0)}
                  >
                    {isSubmitting ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Lista de campos existentes */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Tipos de Campos Cadastrados ({camposTipos.length})</h3>

            {camposTipos.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum tipo de campo cadastrado ainda.
              </div>
            ) : (
              <div className="grid gap-4">
                {camposTipos.map(campo => (
                  <div key={campo.id} className="border rounded-lg p-4 bg-card">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h4 className="font-medium">{campo.nome}</h4>
                          <Badge variant="outline" className="text-xs">
                            {tiposCampo.find(t => t.value === campo.tipo_input)?.label ?? campo.tipo_input}
                          </Badge>
                          {campo.validacao?.required && (
                            <Badge variant="outline" className="text-xs">
                              Obrigatório
                            </Badge>
                          )}
                          {!campo.ativo && (
                            <Badge variant="destructive" className="text-xs">
                              Inativo
                            </Badge>
                          )}
                        </div>

                        {campo.descricao && (
                          <p className="text-sm text-muted-foreground mb-2">{campo.descricao}</p>
                        )}

                        <div className="text-xs text-muted-foreground space-y-1">
                          {campo.placeholder && (
                            <div>
                              <span className="font-medium">Placeholder:</span> {campo.placeholder}
                            </div>
                          )}
                          {campo.valor_padrao && (
                            <div>
                              <span className="font-medium">Valor Padrão:</span> {campo.valor_padrao}
                            </div>
                          )}
                          {campo.opcoes && campo.opcoes.length > 0 && (
                            <div>
                              <span className="font-medium">Opções:</span> {campo.opcoes.map(o => o.label).join(', ')}
                            </div>
                          )}
                          {campo.ajuda && (
                            <div>
                              <span className="font-medium">Ajuda:</span> {campo.ajuda}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(campo)}
                        >
                          <Edit3 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(campo.id)}
                          className="text-red-600 hover:text-red-700 hover:border-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CamposTiposModal;
